/** Endpoint del beacon. Path neutro a propósito: los bloqueadores tiran lo que dice "visit"/"track". */
export const VISIT_BEACON_URL = "/api/pv/r";

export const ADMIN_PATH_RE = /^\/admin(\/|$)/;

/**
 * La app logueada (superficie `app`); el resto del sitio es `site`. Mismo regex que `APP_PATH_RE`
 * en `apps/api/src/site-visits/site-visits.config.ts`: si cambia allá, cambiarlo acá.
 */
export const APP_PATH_RE =
  /^\/(course|mi-aula|library|profile|settings|cart|checkout|orders|progress|qa|credentials)(\/|$)/;

/**
 * Llegó a la pantalla de inscripción o de registro. Misma definición que «Llegó a inscripción» en
 * el panel de sesiones (`reached_signup` en `site-visits.sessions.ts`).
 */
export const SIGNUP_PATH_RE = /\/inscripcion\/?$|^\/auth\/register(\/|$)/;

/**
 * Los CTA del embudo que se cuentan como clic propio (`cta_click`). Mismo formato que acepta la
 * API (`CTA_TARGET_PATTERN`: minúsculas, números y `_`).
 */
export type FunnelCta =
  | "sticky_inscripcion"
  | "sticky_preinscripcion"
  | "card_inscripcion"
  | "header_registrate";

export type VisitBeaconPayload =
  /** El documento se mostró: lo manda el snippet de render y, de respaldo, la app (`VisitBeacon`). */
  | { kind: "document"; visitId: string; path: string; hiddenFirst?: boolean }
  /** `eventId`: si falta, lo pone `sendVisitBeacon` (ver `newEventId`). */
  | { kind: "spa"; path: string; eventId?: string }
  /** Ids de Clarity de la página: `<projectId>.<userId>.<sessionId>.<pageNum>`. */
  | { kind: "clarity"; clarity: string; path: string; visitId?: string }
  | {
      kind: "cta";
      cta: FunnelCta;
      path: string;
      visitId?: string;
      eventId?: string;
      courseId?: string;
      /** Ver `gaBrowser` en `view`. */
      gaBrowser?: true;
    }
  /**
   * Vio la página de un curso / visita comprometida: la API los reenvía a Meta, TikTok y GA4 con
   * `eventId` (el mismo que usa el píxel, con prefijo: ver `conversionEventId`). Obligatorio.
   *
   * `gaBrowser`: el beacon salió sin cookie `_ga` (gtag carga después, asíncrono). La API no puede
   * mandarlo a GA4 sin client_id, así que lo manda el navegador con `gtag` y la API deja GA4 afuera.
   */
  | {
      kind: "view";
      path: string;
      eventId: string;
      visitId?: string;
      courseId?: string;
      gaBrowser?: true;
    }
  | {
      kind: "engaged";
      path: string;
      eventId: string;
      reason: EngagedReason;
      visitId?: string;
      courseId?: string;
      gaBrowser?: true;
    };

/** Mismo formato que acepta la API para sacar el client_id (`parseGaClientId`). */
const GA_COOKIE_RE = /^GA\d\.\d+\.\d+\.\d+$/;

/**
 * Hay cookie `_ga` válida ahora: la API va a poder mandar el evento a GA4 (Measurement Protocol)
 * con el client_id de este navegador. Sin ella (primera visita, gtag todavía no cargó), GA4 lo
 * tiene que mandar el navegador. Nunca tira.
 */
export function hasGaClientCookie(): boolean {
  try {
    if (typeof document === "undefined") return false;
    for (const part of document.cookie.split(";")) {
      const eq = part.indexOf("=");
      if (eq < 0 || part.slice(0, eq).trim() !== "_ga") continue;
      let value = part.slice(eq + 1).trim();
      try {
        value = decodeURIComponent(value);
      } catch {
        // Tal cual.
      }
      if (GA_COOKIE_RE.test(value)) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Qué disparó la visita comprometida: tiempo activo o scroll. */
export type EngagedReason = "time" | "scroll";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * El id de visita del documento que dejó el snippet de render (`window.__acdVid`), si es un UUID.
 * Sin documento registrado (SPA servida sin ingest, tests) no hay: el server une por sesión.
 */
export function documentVisitId(): string | undefined {
  try {
    const value = (window as Window & { __acdVid?: unknown }).__acdVid;
    return typeof value === "string" && UUID_RE.test(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Id de un evento (`spa`, `cta`): viaja igual en el POST, en el `<img>` y en el reenvío del
 * `pagehide`, y la API lo usa para no contar dos veces lo mismo. `crypto.randomUUID()` y, si no
 * está (contexto no seguro, WebView viejo), un UUID v4 con `Math.random` (alcanza: no es secreto).
 */
export function newEventId(): string {
  try {
    const id = crypto.randomUUID();
    if (UUID_RE.test(id)) return id;
  } catch {
    // Sin `crypto.randomUUID`.
  }
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (Number(c) ^ ((Math.random() * 16) >> (Number(c) / 4))).toString(16),
  );
}

/** Más larga que esto, la URL del `<img>` no se arma (la API tampoco la procesaría). */
export const BEACON_IMAGE_URL_MAX = 2000;

/** El POST confirmó SÓLO si es nuestro 204: un 200 lo inventó otro (TTNet, un proxy). */
const confirmedByServer = (response: unknown) =>
  typeof response === "object" &&
  response !== null &&
  (response as { status?: unknown }).status === 204;

/** Beacons que todavía no confirmaron: si la página se cierra, se reenvían (mismo body e ids). */
const inFlight = new Map<number, () => void>();
let inFlightSeq = 0;
let pagehideBound = false;

function sendWithBeacon(body: string): boolean {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      return navigator.sendBeacon(VISIT_BEACON_URL, body);
    }
  } catch {
    // Algunos WebViews tiran en vez de devolver false.
  }
  return false;
}

function bindPagehide() {
  if (pagehideBound || typeof window === "undefined") return;
  pagehideBound = true;
  window.addEventListener("pagehide", () => {
    const pending = [...inFlight.values()];
    inFlight.clear();
    pending.forEach((flush) => flush());
  });
}

/**
 * URL del `<img>` de respaldo (`GET /api/pv/r?k=<tipo>&…`, parámetros cortos: ver
 * `beaconFromQuery` en la API), o null si quedaría demasiado larga.
 */
export function beaconImageUrl(payload: VisitBeaconPayload): string | null {
  const params: Array<[string, string | undefined]> = [["k", payload.kind]];
  switch (payload.kind) {
    case "document":
      params.push(["v", payload.visitId], ["h", payload.hiddenFirst ? "1" : "0"]);
      break;
    case "spa":
      params.push(["e", payload.eventId]);
      break;
    case "clarity":
      params.push(["c", payload.clarity], ["v", payload.visitId]);
      break;
    case "cta":
      params.push(
        ["t", payload.cta],
        ["v", payload.visitId],
        ["e", payload.eventId],
        ["o", payload.courseId],
        ["g", payload.gaBrowser ? "1" : undefined],
      );
      break;
    case "view":
      params.push(
        ["e", payload.eventId],
        ["v", payload.visitId],
        ["o", payload.courseId],
        ["g", payload.gaBrowser ? "1" : undefined],
      );
      break;
    case "engaged":
      params.push(
        ["e", payload.eventId],
        ["v", payload.visitId],
        ["o", payload.courseId],
        ["r", payload.reason],
        ["g", payload.gaBrowser ? "1" : undefined],
      );
      break;
  }
  params.push(["p", payload.path]);
  const query = new URLSearchParams();
  params.forEach(([key, value]) => {
    if (value !== undefined) query.set(key, value);
  });
  const url = `${VISIT_BEACON_URL}?${query.toString()}`;
  return url.length > BEACON_IMAGE_URL_MAX ? null : url;
}

/**
 * Manda el beacon sin bloquear nada. Nunca tira.
 *
 * Diagnóstico del 2026-10-07: en el WebView de TikTok Android, cuando la red nativa (TTNet) trae
 * la página, los POST no salían del teléfono (`sendBeacon`/`keepalive` seguro, y por lo visto
 * tampoco `fetch`), mientras que los GET sí llegaban (los XHR de axios de la app y los recursos).
 * Por eso cada beacon, de cualquier tipo, sale por dos caminos A LA VEZ, con el mismo body e ids
 * (la API es idempotente: render por id de visita, `spa`/`cta` por `eventId`, Clarity por
 * `pageNum`):
 *
 * 1. `fetch` POST (body string ⇒ `text/plain`, sin preflight, sin `keepalive`). Confirma sólo
 *    con 204: un 200 no es nuestro.
 * 2. `<img>` a `GET /api/pv/r?k=…`, en el acto (sin esperar al POST: el único camino probado en
 *    TTNet, y así también cuenta a quien se va antes de que el POST conteste). Confirma si carga
 *    el GIF.
 * 3. `sendBeacon` como último recurso: si el `<img>` falla (o no se puede armar: sin `Image`, URL
 *    demasiado larga) y el POST terminó sin 204 (o no hubo `fetch`).
 *
 * Si la página se cierra (`pagehide`) sin confirmación por ningún camino (p. ej. un POST colgado),
 * sale `sendBeacon` igual: sobrevive al cierre en los navegadores normales.
 */
export function sendVisitBeacon(input: VisitBeaconPayload) {
  try {
    const payload: VisitBeaconPayload =
      (input.kind === "spa" || input.kind === "cta") && !input.eventId
        ? { ...input, eventId: newEventId() }
        : input;
    const body = JSON.stringify(payload);
    const id = ++inFlightSeq;
    let confirmed = false;
    let beaconSent = false;
    // `sendBeacon` sale sólo cuando los dos caminos fallaron: el `<img>` dio error (o no se pudo
    // armar) y el POST terminó sin 204 (o no hubo POST). Un POST colgado espera al `pagehide`.
    let imageFailed = false;
    let postFailed = typeof fetch !== "function";

    const confirm = () => {
      confirmed = true;
      inFlight.delete(id);
    };
    const flushBeacon = () => {
      if (confirmed || beaconSent) return;
      beaconSent = true;
      inFlight.delete(id);
      sendWithBeacon(body);
    };
    const failImage = () => {
      imageFailed = true;
      if (postFailed) flushBeacon();
    };
    const failPost = () => {
      postFailed = true;
      if (imageFailed) flushBeacon();
    };

    bindPagehide();
    inFlight.set(id, flushBeacon);

    if (!postFailed) {
      try {
        fetch(VISIT_BEACON_URL, { method: "POST", body, credentials: "same-origin" }).then(
          (response) => (confirmedByServer(response) ? confirm() : failPost()),
          failPost,
        );
      } catch {
        failPost();
      }
    }

    const url = beaconImageUrl(payload);
    try {
      if (url === null || typeof Image !== "function") {
        failImage();
      } else {
        const img = new Image();
        img.onload = confirm;
        img.onerror = failImage;
        img.src = url;
      }
    } catch {
      failImage();
    }
  } catch {
    // La medición nunca rompe la página.
  }
}

/** Sólo para tests. */
export function __resetVisitBeaconForTests() {
  inFlight.clear();
}

/**
 * Clic propio en un CTA del embudo: llega aunque no haya Clarity ni consentimiento (es el registro
 * propio, igual que el beacon `spa`). Nunca desde el admin. `eventId` = el mismo que usa el píxel
 * (`cta:<eventId>`): la API lo reenvía a las plataformas y ellas cuentan uno solo.
 */
export function sendCtaBeacon(
  cta: FunnelCta,
  eventId?: string,
  courseId?: string,
  gaBrowser?: boolean,
) {
  try {
    if (typeof window === "undefined") return;
    const path = window.location.pathname;
    if (ADMIN_PATH_RE.test(path)) return;
    const visitId = documentVisitId();
    sendVisitBeacon({
      kind: "cta",
      cta,
      path,
      ...(visitId ? { visitId } : {}),
      ...(eventId ? { eventId } : {}),
      ...(courseId ? { courseId } : {}),
      ...(gaBrowser ? { gaBrowser: true as const } : {}),
    });
  } catch {
    // La medición nunca rompe la página.
  }
}

/**
 * Vio un curso o tuvo una visita comprometida: beacon propio para que la API lo mande a las
 * plataformas aunque el píxel esté bloqueado. Nunca desde el admin. Nunca tira.
 */
export function sendFunnelBeacon(
  input:
    | { kind: "view"; eventId: string; courseId?: string; gaBrowser?: boolean }
    | {
        kind: "engaged";
        eventId: string;
        reason: EngagedReason;
        courseId?: string;
        gaBrowser?: boolean;
      },
) {
  try {
    if (typeof window === "undefined") return;
    const path = window.location.pathname;
    if (ADMIN_PATH_RE.test(path)) return;
    const visitId = documentVisitId();
    const base = {
      path,
      eventId: input.eventId,
      ...(visitId ? { visitId } : {}),
      ...(input.courseId ? { courseId: input.courseId } : {}),
      ...(input.gaBrowser ? { gaBrowser: true as const } : {}),
    };
    sendVisitBeacon(
      input.kind === "view"
        ? { kind: "view", ...base }
        : { kind: "engaged", reason: input.reason, ...base },
    );
  } catch {
    // La medición nunca rompe la página.
  }
}
